import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

interface CarOption {
  label: string;
  priceDE: number;
  priceES: number;
}

const carOptions: CarOption[] = [
  { label: 'BMW M4 Competition', priceDE: 82500, priceES: 108000 },
  { label: 'Volkswagen Golf R', priceDE: 38900, priceES: 51000 },
  { label: 'Audi RS3 Sportback', priceDE: 63500, priceES: 82000 },
  { label: 'Audi RS6 Avant', priceDE: 119000, priceES: 152000 },
  { label: 'Mercedes AMG C63', priceDE: 75900, priceES: 98000 },
  { label: 'BMW M2 Coupé', priceDE: 68900, priceES: 88000 },
  { label: 'Porsche 911 Carrera 4S', priceDE: 156900, priceES: 198000 },
  { label: 'Porsche Cayenne', priceDE: 94500, priceES: 122000 },
  { label: 'Mercedes AMG A45 S', priceDE: 71900, priceES: 93000 },
  { label: 'Personalizado', priceDE: 0, priceES: 0 },
];

// Import costs (fixed estimates)
const TRANSPORT_COST = 800;
const MANAGEMENT_COST = 600;
const ZSCARS_FEE = 1400;

function animateValue(
  start: number,
  end: number,
  duration: number,
  setter: (v: number) => void
) {
  const startTime = performance.now();
  const update = (now: number) => {
    const elapsed = now - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const eased = 1 - Math.pow(1 - progress, 3);
    setter(Math.round(start + (end - start) * eased));
    if (progress < 1) requestAnimationFrame(update);
  };
  requestAnimationFrame(update);
}

export default function SavingsSimulator() {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLDivElement>(null);
  const [revealed, setRevealed] = useState(false);

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [customPriceDE, setCustomPriceDE] = useState(50000);
  const [customPriceES, setCustomPriceES] = useState(65000);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const isCustom = selectedIndex === carOptions.length - 1;
  const priceDE = isCustom ? customPriceDE : carOptions[selectedIndex].priceDE;
  const priceES = isCustom ? customPriceES : carOptions[selectedIndex].priceES;

  const totalImportCost = priceDE + TRANSPORT_COST + MANAGEMENT_COST + ZSCARS_FEE;
  const rawSavings = priceES - totalImportCost;
  const savings = Math.max(0, rawSavings);
  const savingsPct = priceES > 0 ? Math.round((savings / priceES) * 100) : 0;

  // Animated display values
  const [dispSavings, setDispSavings] = useState(savings);
  const [dispTotal, setDispTotal] = useState(totalImportCost);
  const [dispES, setDispES] = useState(priceES);

  useEffect(() => {
    animateValue(dispSavings, savings, 600, setDispSavings);
    animateValue(dispTotal, totalImportCost, 600, setDispTotal);
    animateValue(dispES, priceES, 600, setDispES);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savings, totalImportCost, priceES]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) setRevealed(true);
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const fmt = (n: number) =>
    n.toLocaleString('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });

  return (
    <section
      ref={sectionRef}
      className="py-20 sm:py-28 bg-[#050505] relative overflow-hidden"
      id="simulador"
    >
      {/* Background glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[500px] rounded-full bg-purple-900/8 blur-3xl pointer-events-none" />
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-purple-500/30 to-transparent" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        {/* Header */}
        <div
          className="text-center mb-14"
          style={{
            opacity: revealed ? 1 : 0,
            transform: revealed ? 'translateY(0)' : 'translateY(40px)',
            transition: 'opacity 0.8s ease, transform 0.8s ease',
          }}
        >
          <div className="inline-flex items-center gap-2 border border-purple-500/30 px-4 py-2 rounded-full mb-6">
            <i className="ri-calculator-line text-purple-400 text-sm" />
            <span className="text-purple-400 text-xs tracking-widest uppercase font-medium">
              {t('simulator.label')}
            </span>
          </div>
          <h2
            className="font-display text-4xl sm:text-5xl lg:text-6xl text-white mb-4"
            style={{ letterSpacing: '0.03em' }}
          >
            {t('simulator.title1')}{' '}
            <span className="gradient-text-purple">{t('simulator.title2')}</span>
          </h2>
          <p className="font-body text-white/50 text-base max-w-2xl mx-auto leading-relaxed">
            {t('simulator.subtitle')}
          </p>
        </div>

        <div
          className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 items-start"
          style={{
            opacity: revealed ? 1 : 0,
            transform: revealed ? 'translateY(0)' : 'translateY(50px)',
            transition: 'opacity 0.8s ease 0.2s, transform 0.8s ease 0.2s',
          }}
        >
          {/* LEFT — Controls */}
          <div className="glass-card rounded-2xl p-6 sm:p-8 space-y-7">
            <h3 className="font-heading text-xl text-white" style={{ letterSpacing: '0.04em' }}>
              {t('simulator.configure')}
            </h3>

            {/* Car selector */}
            <div className="space-y-2">
              <label className="font-label text-xs text-white/50 tracking-widest uppercase">
                {t('simulator.car')}
              </label>
              <div className="relative">
                <button
                  onClick={() => setDropdownOpen((v) => !v)}
                  className="w-full flex items-center justify-between bg-white/5 border border-white/10 hover:border-purple-500/40 rounded-xl px-4 py-3 text-white text-sm transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  <span className="font-body">{carOptions[selectedIndex].label}</span>
                  <i className={`ri-arrow-down-s-line text-purple-400 transition-transform duration-300 ${dropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                {dropdownOpen && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-[#0e0e0e] border border-purple-500/20 rounded-xl overflow-hidden z-30">
                    {carOptions.map((car, i) => (
                      <button
                        key={i}
                        onClick={() => { setSelectedIndex(i); setDropdownOpen(false); }}
                        className={`w-full text-left px-4 py-3 text-sm font-body transition-colors duration-200 cursor-pointer whitespace-nowrap ${
                          selectedIndex === i
                            ? 'bg-purple-900/40 text-purple-300'
                            : 'text-white/70 hover:bg-white/5 hover:text-white'
                        }`}
                      >
                        {car.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Custom price inputs */}
            {isCustom && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="font-label text-xs text-white/50 tracking-widest uppercase">
                    {t('simulator.price.de')}
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 text-sm">€</span>
                    <input
                      type="number"
                      value={customPriceDE}
                      onChange={(e) => setCustomPriceDE(Number(e.target.value))}
                      className="w-full bg-white/5 border border-white/10 focus:border-purple-500/50 rounded-xl pl-7 pr-3 py-3 text-white text-sm font-body outline-none transition-colors duration-300"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="font-label text-xs text-white/50 tracking-widest uppercase">
                    {t('simulator.price.es')}
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 text-sm">€</span>
                    <input
                      type="number"
                      value={customPriceES}
                      onChange={(e) => setCustomPriceES(Number(e.target.value))}
                      className="w-full bg-white/5 border border-white/10 focus:border-purple-500/50 rounded-xl pl-7 pr-3 py-3 text-white text-sm font-body outline-none transition-colors duration-300"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Cost breakdown */}
            <div className="space-y-3">
              <p className="font-label text-xs text-white/40 tracking-widest uppercase mb-4">
                {t('simulator.breakdown')}
              </p>

              {/* Vehicle price */}
              <div className="flex items-center justify-between">
                <span className="font-body text-sm text-white">{t('simulator.item.price')}</span>
                <span className="font-body text-sm text-white/80">{fmt(priceDE)}</span>
              </div>

              {/* Transport */}
              <div className="flex items-center justify-between">
                <div className="flex items-baseline gap-1">
                  <span className="font-body text-sm text-white/70">{t('simulator.item.transport')}</span>
                  <span className="font-body text-xs text-white/30">({t('simulator.item.transport.sub')} {fmt(TRANSPORT_COST)})</span>
                </div>
                <span className="font-body text-sm text-white/80">{fmt(TRANSPORT_COST)}</span>
              </div>

              {/* Management */}
              <div className="flex items-center justify-between">
                <span className="font-body text-sm text-white/70">{t('simulator.item.management')}</span>
                <span className="font-body text-sm text-white/80">{fmt(MANAGEMENT_COST)}</span>
              </div>

              {/* Taxes — variable */}
              <div className="flex items-center justify-between">
                <div className="flex items-baseline gap-1">
                  <span className="font-body text-sm text-white/70">{t('simulator.item.taxes')}</span>
                </div>
                <span className="font-body text-xs text-white/40 italic">{t('simulator.item.taxes.sub')}</span>
              </div>

              {/* ZS Cars fee */}
              <div className="flex items-center justify-between">
                <span className="font-body text-sm text-white/70">{t('simulator.item.zscars')}</span>
                <span className="font-body text-sm text-white/80">{fmt(ZSCARS_FEE)}</span>
              </div>

              <div className="h-px bg-gradient-to-r from-purple-500/30 to-transparent my-2" />

              {/* Total import */}
              <div className="flex items-center justify-between">
                <span className="font-heading text-base text-white" style={{ letterSpacing: '0.04em' }}>
                  {t('simulator.total.import')}
                </span>
                <div className="flex items-baseline gap-1">
                  <span className="font-display text-xl text-purple-400">{fmt(dispTotal)}</span>
                  <span className="font-body text-xs text-purple-400/60">{t('simulator.total.import.approx') || 'aprox.'}</span>
                </div>
              </div>

              {/* Spain price */}
              <div className="flex items-center justify-between">
                <span className="font-body text-sm text-white/50">{t('simulator.total.spain')}</span>
                <span className="font-body text-sm text-white/50 line-through">{fmt(dispES)}</span>
              </div>

              {/* Note */}
              <div className="mt-3 pt-3 border-t border-white/5">
                <p className="font-body text-xs text-white/30 leading-relaxed italic">
                  {t('simulator.note')}
                </p>
              </div>
            </div>

            {/* CTA */}
            <a
              href="#contacto"
              onClick={(e) => {
                e.preventDefault();
                document.querySelector('#contacto')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="flex items-center justify-center gap-2 gradient-purple text-white font-semibold tracking-wider uppercase px-6 py-4 rounded-full glow-purple hover:scale-105 transition-all duration-300 cursor-pointer whitespace-nowrap text-sm w-full"
            >
              <i className="ri-car-line" />
              {t('simulator.cta')}
            </a>
          </div>

          {/* RIGHT — Results */}
          <div className="space-y-4">
            {/* Big savings card */}
            <div className="relative glass-card rounded-2xl p-8 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 to-transparent pointer-events-none" />
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-purple-600 to-purple-400" />
              <div className="relative z-10">
                <p className="font-label text-xs text-purple-400 tracking-widest uppercase mb-3">
                  {t('simulator.savings.label')}
                </p>
                <div className="flex items-end gap-3 mb-2">
                  <span
                    className="font-display text-6xl sm:text-7xl gradient-text-purple"
                    style={{ letterSpacing: '0.02em', lineHeight: 1 }}
                  >
                    {fmt(dispSavings)}
                  </span>
                </div>
                <p className="font-body text-white/40 text-sm">
                  {t('simulator.savings.desc', { pct: savingsPct })}
                </p>

                {/* Progress bar */}
                <div className="mt-6">
                  <div className="flex justify-between text-xs font-body text-white/40 mb-2">
                    <span>{t('simulator.import.cost')}</span>
                    <span>{t('simulator.spain.cost')}</span>
                  </div>
                  <div className="relative h-3 bg-white/5 rounded-full overflow-hidden">
                    <div
                      className="absolute left-0 top-0 h-full gradient-purple rounded-full transition-all duration-700 ease-out"
                      style={{ width: priceES > 0 ? `${Math.min((totalImportCost / priceES) * 100, 100)}%` : '0%' }}
                    />
                    <div className="absolute right-0 top-0 h-full bg-white/10 rounded-full"
                      style={{ width: priceES > 0 ? `${Math.min((savings / priceES) * 100, 100)}%` : '0%' }}
                    />
                  </div>
                  <div className="flex justify-between text-xs font-body mt-2">
                    <span className="text-purple-400">{fmt(dispTotal)}</span>
                    <span className="text-white/30 line-through">{fmt(dispES)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Stats grid */}
            <div className="grid grid-cols-3 gap-3">
              {[
                { icon: 'ri-time-line', valueKey: '15-21', labelKey: 'simulator.stat.days' },
                { icon: 'ri-shield-check-line', valueKey: '100%', labelKey: 'simulator.stat.certified' },
                { icon: 'ri-map-pin-line', valueKey: 'EU', labelKey: 'simulator.stat.origin' },
              ].map((stat) => (
                <div key={stat.labelKey} className="glass-card rounded-xl p-4 text-center">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-purple-900/30 mx-auto mb-2">
                    <i className={`${stat.icon} text-purple-400 text-base`} />
                  </div>
                  <p className="font-display text-xl text-white mb-1" style={{ letterSpacing: '0.03em' }}>
                    {stat.valueKey}
                  </p>
                  <p className="font-body text-white/40 text-xs">{t(stat.labelKey)}</p>
                </div>
              ))}
            </div>

            {/* Disclaimer */}
            <div className="glass-card rounded-xl px-5 py-4 flex items-start gap-3">
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-information-line text-purple-400 text-base" />
              </div>
              <p className="font-body text-white/35 text-xs leading-relaxed">
                {t('simulator.disclaimer')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}